import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, Dropdown, useClassNames, useToast } from '@h2oai/ui-kit';
import { FormEvent, useCallback, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  FeatureSetPermissionRequest,
  PermissionType,
  ProjectPermissionRequest,
  ResourceType,
  SubmitPendingPermissionRequest,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { AccessInfo, ContentsTitle, permissionTypeNameMap } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { TextListEditor } from '../TextListEditor/TextListEditor';
import {
  grantPermissionStylesDefault,
  IGrantPermissionClassNames,
  IGrantPermissionStyles,
} from './GrantPermission.styles';

const getPermissionTypeOptions = (currentPermission?: PermissionType, higherPermission?: boolean) => {
  const options: IDropdownOption[] = [];
  let isHigherPermission = false;
  permissionTypeNameMap.forEach((text, key) => {
    if (higherPermission) {
      if (isHigherPermission || !currentPermission) {
        options.push({ key, text });
      } else {
        if (!isHigherPermission && key === currentPermission) {
          isHigherPermission = true;
        }
      }
    } else {
      options.push({ key, text });
    }
  });
  return options;
};

interface IGrantPermissionProps {
  id?: string;
  resourceType?: ResourceType;
  accessInfo?: AccessInfo;
  requestHigherPermission?: boolean;
  title?: string;
  promote?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
}

export const GrantPermission = ({
  id,
  resourceType,
  accessInfo,
  requestHigherPermission,
  title,
  promote,
  onSave,
  onCancel,
}: IGrantPermissionProps) => {
  const [values, setValues] = useState<ProjectPermissionRequest | FeatureSetPermissionRequest>({});
  const { addToast } = useToast();
  const onUpdate = useCallback(
    async (updatedValues: ProjectPermissionRequest & FeatureSetPermissionRequest & SubmitPendingPermissionRequest) => {
      try {
        let api;
        if (resourceType === ResourceType.PROJECT) {
          if (requestHigherPermission && !promote) {
            api = CoreService.SubmitPendingProjectPermission;
            updatedValues.resourceId = id;
          } else {
            api = CoreService.AddProjectPermission;
            updatedValues.projectId = id;
          }
        } else {
          if (requestHigherPermission && !promote) {
            api = CoreService.SubmitPendingFeatureSetPermission;
            updatedValues.resourceId = id;
          } else {
            api = CoreService.AddFeatureSetPermission;
            updatedValues.featureSetId = id;
          }
        }
        if (!requestHigherPermission || promote) {
          if (accessInfo?.email) {
            updatedValues.userEmails = [accessInfo?.email];
          }
        }
        await fsApi(api, updatedValues);
        addToast({
          messageBarType: MessageBarType.success,
          message: requestHigherPermission
            ? 'You have successfully requested access!'
            : 'You have successfully granted access to the selected users!',
        });
      } catch (err) {
        addToast(
          getErrorMessageBarMessage(
            err,
            `An error has occurred while ${requestHigherPermission ? 'requesting' : 'granting'} access: `
          )
        );
      } finally {
        onSave && onSave();
      }
    },
    []
  );
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      onUpdate(values);
      e.preventDefault();
    },
    [values]
  );
  const classNames = useClassNames<IGrantPermissionStyles, IGrantPermissionClassNames>(
    'GrantPermission',
    grantPermissionStylesDefault
  );
  return (
    <div className={classNames.root} data-test="request-permission">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          <Dropdown
            label="Access Type"
            required
            placeholder="Select Access Type"
            options={getPermissionTypeOptions(accessInfo?.permission, requestHigherPermission || promote)}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, permission: option.key as PermissionType });
              }
            }}
          />
          {!requestHigherPermission && !promote && (
            <TextListEditor
              key="userEmails"
              placeholder="Input Email & Enter"
              items={values.userEmails}
              onChange={(userEmails) => setValues({ ...values, userEmails })}
            />
          )}
        </div>
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
          <Button
            styles={buttonStylesPrimary}
            type="submit"
            text={title}
            disabled={!values.permission || (title === ContentsTitle.grantAccess && !values.userEmails)}
          />
        </div>
      </form>
    </div>
  );
};
